import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { SlCheckbox, SlIcon } from '@shoelace-style/shoelace/dist/react';
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Pagination } from '../Pagination';
import { getNestedValue } from '../utils';
import { DnDSubRowProps } from './DnDSubRow';
import DnDTableBodyRow from './DnDTableBodyRow';

export interface ColumnData<T extends Record<string, any> = {}> {
  header: string;
  field: string;
  id: string;
  sort?: boolean;
  width?: number;
  renderer?: (data: T) => React.ReactNode;
  headerRenderer?: (data: Pick<ColumnData<T>, 'field' | 'sort' | 'header' | 'id'>) => React.ReactNode;
  show?: boolean;
}

export interface DnDTableProps<T> {
  data: Array<T & { subRows?: Array<T> }>;
  columns: Array<ColumnData<T>>;
  rowSelection?: boolean;
  onSelectionChange?: (data: Array<T>) => void;
  noDataText: string;
  rowsPerPage?: number;
  variant?: 'filled' | 'outlined';
  expandable?: boolean;
  subrowProps?: DnDSubRowProps;
  draggable?: boolean;
  onReorder?: (reorderedData: Array<T>) => void;
}

const uuid = () => Math.random().toString(16).slice(-8);

const DnDTableComponent = <T extends Record<string, any>>(props: DnDTableProps<T>) => {
  const locale = localStorage.getItem('co4:app/language') ?? 'de';
  const [t] = useTranslation();
  const {
    data: ogData,
    columns,
    rowSelection = false,
    onSelectionChange = () => {},
    noDataText = t('noRecordsFound'),
    rowsPerPage = 10,
    variant = 'outlined',
    expandable = false,
    draggable = true,
    onReorder = () => {},
  } = props;
  const [selection, setSelection] = React.useState<Array<string>>([]);
  const [sort, setSort] = React.useState({ field: '', order: '' });
  const [pagination, setPagination] = React.useState({
    currentPage: 1,
    rowsPerPage,
    totalPages: 1,
  });
  const [activeId, setActiveId] = React.useState<string | null>(null);
  const [originalData, setOriginalData] = React.useState<Array<T & { selectionId?: string }>>([]);
  const [currentPageData, setCurrentPageData] = React.useState<Array<T & { selectionId?: string }>>([]);

  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 250,
        tolerance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const data: Array<T & { selectionId?: string }> = React.useMemo(() => {
    setPagination(({ rowsPerPage }) => ({
      currentPage: 1,
      rowsPerPage,
      totalPages: Math.ceil(ogData.length / rowsPerPage),
    }));

    const dataWithIds = ogData.map((d) => ({ ...d, selectionId: uuid() }));
    setOriginalData(dataWithIds);
    return dataWithIds;
  }, [ogData]);

  const currentRange = React.useMemo(() => {
    const start = (pagination.currentPage - 1) * pagination.rowsPerPage;
    const end = start + pagination.rowsPerPage > data.length ? data.length : start + pagination.rowsPerPage;
    return { start, end };
  }, [pagination, data.length]);

  const sortBy = React.useCallback(
    (a, b) => {
      const { field, order } = sort;
      if (!field || !order) return 0;

      const column = columns.find((col) => col.field === field);
      if (!column) return 0;

      // If the column has a custom renderer, we need to handle it differently
      if (column.renderer) {
        const valA = column.renderer(a);
        const valB = column.renderer(b);

        // Handle React elements by extracting data-value or actual data
        if (React.isValidElement(valA) || React.isValidElement(valB)) {
          // Try to get the data-value attribute if it exists
          const getDataValue = (element: React.ReactElement) => {
            if (element.props['data-value']) return element.props['data-value'];
            if (element.props['data-production'] !== undefined) return element.props['data-production'];
            return element.props.children;
          };

          const finalValA = React.isValidElement(valA) ? getDataValue(valA) : valA;
          const finalValB = React.isValidElement(valB) ? getDataValue(valB) : valB;

          if (typeof finalValA === 'string' && typeof finalValB === 'string') {
            return order === 'asc'
              ? finalValA.localeCompare(finalValB, locale)
              : finalValB.localeCompare(finalValA, locale);
          }

          if (typeof finalValA === 'number' && typeof finalValB === 'number') {
            return order === 'asc' ? finalValA - finalValB : finalValB - finalValA;
          }

          if (typeof finalValA === 'boolean' && typeof finalValB === 'boolean') {
            return order === 'asc'
              ? finalValA === finalValB
                ? 0
                : finalValA
                ? -1
                : 1
              : finalValA === finalValB
              ? 0
              : finalValA
              ? 1
              : -1;
          }

          return 0;
        }

        // Handle string/number values from renderers
        if (typeof valA === 'string' && typeof valB === 'string') {
          return order === 'asc' ? valA.localeCompare(valB, locale) : valB.localeCompare(valA, locale);
        }

        if (typeof valA === 'number' && typeof valB === 'number') {
          return order === 'asc' ? valA - valB : valB - valA;
        }

        return 0;
      }

      // For regular fields, handle nested paths
      const valA = getNestedValue(a, field);
      const valB = getNestedValue(b, field);

      // Handle arrays (like details.name[0].value)
      const finalValA = Array.isArray(valA) ? valA[0]?.value ?? valA : valA;
      const finalValB = Array.isArray(valB) ? valB[0]?.value ?? valB : valB;

      const multiplier = order === 'asc' ? 1 : -1;

      if (typeof finalValA === 'number' && typeof finalValB === 'number') {
        return order === 'asc' ? finalValA - finalValB : finalValB - finalValA;
      } else if (typeof finalValA === 'string' && typeof finalValB === 'string') {
        return multiplier * finalValA.localeCompare(finalValB, locale);
      } else return 0;
    },
    [sort, locale, columns],
  );

  React.useEffect(() => {
    if (!sort.field || !sort.order) {
      // If no sorting is applied, use the original data order
      setCurrentPageData(originalData.slice(currentRange.start, currentRange.end));
    } else {
      // Apply sorting when sort is active
      setCurrentPageData([...data].sort(sortBy).slice(currentRange.start, currentRange.end));
    }
  }, [data, sortBy, currentRange, sort, originalData]);

  const handleHeaderClick = React.useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      const target = e.target as HTMLElement;
      const headerElement = target.closest('[id]');
      const field = headerElement?.id;
      if (!field) return;

      // If clicking on the same field
      if (field === sort.field) {
        // Toggle between ascending, descending, and no sort
        if (sort.order === 'asc') {
          setSort({ field, order: 'desc' });
        } else if (sort.order === 'desc') {
          // On third click, remove sorting completely
          setSort({ field: '', order: '' });
        } else {
          setSort({ field, order: 'asc' });
        }
      } else {
        // If clicking on a different field, start with ascending sort
        setSort({ field, order: 'asc' });
      }
    },
    [sort],
  );

  const handleSelectionChange = (id: string | null, all?: boolean) => {
    let updatedSelections = [...selection];

    if (all !== undefined) {
      updatedSelections = all ? currentPageData.map((d) => d.selectionId!) : [];
    } else if (id !== null) {
      if (updatedSelections.includes(id)) {
        updatedSelections.splice(updatedSelections.indexOf(id), 1);
      } else {
        updatedSelections.push(id);
      }
    }

    const selectionData = all
      ? [...currentPageData]
      : updatedSelections.map((sel) => currentPageData.find((d) => d.selectionId === sel)!).filter(Boolean);

    onSelectionChange(selectionData as T[]);
    setSelection(updatedSelections);
  };

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      setCurrentPageData((items) => {
        const oldIndex = items.findIndex((item) => item.selectionId === active.id);
        const newIndex = items.findIndex((item) => item.selectionId === over.id);

        const newItems = arrayMove(items, oldIndex, newIndex);

        // Update the entire data array and notify parent
        const newData = [...data];
        const startIndex = currentRange.start;

        for (let i = 0; i < newItems.length; i++) {
          newData[startIndex + i] = newItems[i];
        }

        onReorder(newData as T[]);

        return newItems;
      });
    }

    setActiveId(null);
  };

  let areAllSelected =
    rowSelection && selection.length !== 0 && currentPageData.every((d) => selection.includes(d.selectionId!));

  const onPageChange = (currentPage: number) => {
    setPagination((prev) => ({ ...prev, currentPage }));
  };

  const onRowsChange = (rowsPerPage: number) => {
    setPagination({
      currentPage: 1,
      rowsPerPage,
      totalPages: Math.ceil(data.length / rowsPerPage),
    });
  };

  const renderDragOverlay = useCallback(() => {
    if (!activeId) return null;
    const activeItem = currentPageData.find((item) => item.selectionId === activeId);
    if (!activeItem) return null;

    return (
      <table>
        <tbody>
          <tr>
            {draggable && (
              <td className="drag-handle-td">
                <SlIcon name="grip-vertical" />
              </td>
            )}
            {rowSelection && (
              <td>
                <div className="selection-box head-col">
                  <SlCheckbox />
                </div>
              </td>
            )}
            {expandable && <td className="expand-button-td"></td>}
            {columns.map((col) => {
              if (col.show === false) return null;
              return (
                <td key={col.id}>
                  {col.renderer
                    ? col.renderer({ ...activeItem, rowValue: getNestedValue(activeItem, col.field) })
                    : getNestedValue(activeItem, col.field) ?? '-'}
                </td>
              );
            })}
          </tr>
        </tbody>
      </table>
    );
  }, [activeId, currentPageData, columns, draggable, rowSelection, expandable]);

  return (
    <>
      <div className={`dnd-table-wrapper ${variant}`}>
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          modifiers={[restrictToVerticalAxis]}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}>
          <table>
            <thead>
              <tr>
                {draggable && <th className="drag-handle-td"></th>}
                {rowSelection && (
                  <th id="select-all-box">
                    <div id="select-all" className="head-col selection-box ">
                      <SlCheckbox
                        disabled={!data.length}
                        checked={areAllSelected}
                        onSlChange={() => {
                          handleSelectionChange(null, !areAllSelected);
                        }}></SlCheckbox>
                    </div>
                  </th>
                )}
                {expandable && <th className="expand-button-td"></th>}

                {columns.map((col) => {
                  const { id, field, header, headerRenderer, width = 'auto', sort: colSort = true, show } = col;
                  let icon = 'list';
                  if (sort.field === field) {
                    icon = sort.order === 'desc' ? 'sort-alpha-up-alt' : 'sort-alpha-down';
                  }

                  if (show === false) return null;

                  return (
                    <th
                      key={id}
                      id={field}
                      style={{ width }}
                      onClick={(event) => {
                        if (colSort) {
                          handleHeaderClick(event);
                        }
                      }}>
                      {headerRenderer ? (
                        headerRenderer({ field, header, id, sort: colSort })
                      ) : (
                        <div id={field} className="head-col" data-value={header}>
                          {header}
                          {colSort && <SlIcon name={icon}></SlIcon>}
                        </div>
                      )}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {data.length > 0 ? (
                <SortableContext
                  items={currentPageData.map((item) => item.selectionId!)}
                  strategy={verticalListSortingStrategy}>
                  {currentPageData.map((item, index) => (
                    <DnDTableBodyRow
                      key={`dnd-table-row-${item.id}-${item.selectionId}`}
                      id={item.selectionId!}
                      expandable={expandable}
                      rowSelection={rowSelection}
                      selected={selection.includes(item.selectionId!)}
                      onCheckboxToggle={handleSelectionChange}
                      columns={columns}
                      subrowProps={props?.subrowProps ?? ({} as DnDSubRowProps)}
                      index={index}
                      data={item}
                      draggable={draggable}
                    />
                  ))}
                </SortableContext>
              ) : (
                <tr>
                  <td colSpan={columns.length + +rowSelection + +expandable + +draggable}>{noDataText}</td>
                </tr>
              )}
            </tbody>
          </table>
          <DragOverlay>{renderDragOverlay()}</DragOverlay>
        </DndContext>
      </div>
      {(pagination.totalPages > 1 || pagination.rowsPerPage !== rowsPerPage) && (
        <Pagination
          currentRange={currentRange}
          totalData={data.length}
          totalPages={pagination.totalPages}
          rowsPerPage={pagination.rowsPerPage}
          onPageChange={onPageChange}
          onRowsChange={onRowsChange}
          activePage={pagination.currentPage}
        />
      )}
    </>
  );
};

export const DnDTable = React.memo(DnDTableComponent) as typeof DnDTableComponent;
